/**
 * Size control for a placed instance of a length-parametrised module.
 *
 * Modules like `module_jaxon_pipe_22mm` are a family, not a part: two
 * fixed hubs with a length of pipe between them that the user picks.
 * Dropping one places the default member of the family; this is where
 * that choice gets revised afterwards, without losing the instance's
 * connections or having to delete and re-place it.
 *
 * The server owns the geometry: it builds (or reuses) a module at the
 * requested length and swaps it in behind the same instance id. So this
 * only has to send a number, and it sends it on release rather than on
 * every drag frame -- each distinct value is a mesh regeneration.
 */

import { useEffect, useState } from "react";
import { Box, Group, NumberInput, Slider, Text, Tooltip } from "@mantine/core";

import type { ModuleData } from "../../protocol.generated";

/** One dimension a module declares as variable, as sent by the server. */
export interface ParameterSpec {
  name: string;
  /** Metres. The shortest the mesh can be made; never null. */
  min: number;
  /** Metres, or null for unbounded -- a longer link is just longer stock. */
  max: number | null;
  default: number;
  step: number;
  description: string;
}

/** How far past the current value the slider reaches when there is no maximum. */
const OPEN_ENDED_HEADROOM = 2.0;

const toMillimetres = (metres: number) => Math.round(metres * 1000 * 1000) / 1000;

/**
 * Find the parametric family a placed module belongs to.
 *
 * @param modules - the module library, as sent by the server
 * @param moduleId - the module a placed instance is currently using
 * @returns the base module and the values this variant was built at,
 *   or null when the instance is of an ordinary fixed-size module
 */
export function resolveParametric(
  modules: ModuleData[],
  moduleId: string | null
): { base: ModuleData; specs: ParameterSpec[]; values: Record<string, number> } | null {
  if (!moduleId) return null;
  const placed = modules.find((m) => m.id === moduleId);
  if (!placed?.generated_from) return null;
  const base = modules.find((m) => m.id === placed.generated_from);
  if (!base) return null;
  const specs = (base.parameters ?? []) as unknown as ParameterSpec[];
  if (specs.length === 0) return null;
  return { base, specs, values: placed.generated_parameters ?? {} };
}

interface ParameterControlProps {
  spec: ParameterSpec;
  /** Metres. What the placed variant was built at. */
  value: number;
  /** Called with metres, once the user has settled on a value. */
  onCommit: (value: number) => void;
}

export function ParameterControl({ spec, value, onCommit }: ParameterControlProps) {
  // Millimetres throughout the control: the underlying dimensions are
  // machined parts, and 0.13 m is harder to reason about than 130 mm.
  const [draft, setDraft] = useState(() => toMillimetres(value));

  // A committed change comes back as a new variant, so follow the
  // server rather than trusting the local draft.
  useEffect(() => {
    setDraft(toMillimetres(value));
  }, [value]);

  const minMm = toMillimetres(spec.min);
  const maxMm = spec.max === null ? null : toMillimetres(spec.max);
  const stepMm = Math.max(toMillimetres(spec.step), 0.1);
  // With no upper bound the slider still needs an end; give it room to
  // grow well past where the module is now, and let the number input
  // take anything beyond that.
  const sliderMax = maxMm ?? Math.max(draft * OPEN_ENDED_HEADROOM, minMm + OPEN_ENDED_HEADROOM * 100);

  const clamp = (mm: number) => {
    const floored = Math.max(mm, minMm);
    return maxMm === null ? floored : Math.min(floored, maxMm);
  };

  const commit = (mm: number) => {
    const settled = clamp(mm);
    setDraft(settled);
    if (Math.abs(settled - toMillimetres(value)) > 1e-6) {
      onCommit(settled / 1000);
    }
  };

  const label = spec.name.charAt(0).toUpperCase() + spec.name.slice(1);

  return (
    <Box>
      <Group justify="space-between" align="center" mb={4}>
        <Tooltip label={spec.description} disabled={!spec.description} withArrow>
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Tooltip>
        <Text size="xs" c="dimmed">
          min {minMm.toFixed(1)} mm{maxMm === null ? " · no max" : ` · max ${maxMm.toFixed(1)} mm`}
        </Text>
      </Group>
      <Group gap="xs" align="center" wrap="nowrap">
        <Slider
          style={{ flex: 1 }}
          size="sm"
          min={minMm}
          max={sliderMax}
          step={stepMm}
          value={Math.min(draft, sliderMax)}
          onChange={setDraft}
          onChangeEnd={commit}
          label={(v) => `${v.toFixed(1)} mm`}
        />
        <NumberInput
          size="xs"
          w={92}
          min={minMm}
          max={maxMm ?? undefined}
          step={stepMm}
          decimalScale={1}
          value={draft}
          suffix=" mm"
          onChange={(next) => setDraft(typeof next === "number" ? next : draft)}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commit(draft);
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      </Group>
    </Box>
  );
}
